/**
 * DP269-A — the Site Office: one party, one project.
 *
 * Two ways in, one page:
 *   /site-office/:token                       the party's own link — the token the
 *                                             agreement was signed through (DP268-D).
 *                                             GET /api/site-office-links/{token}
 *   /projects/:projectId/site-office/:membershipId
 *                                             a staff user pressing "Open as …" on the
 *                                             team row. GET /api/projects/{pid}/memberships/{mid}/site-office
 *                                             (require_auth). Same payload with admin: true.
 *
 * The server decides the face: status "unsigned" (only the door to the agreement),
 * "hold" (only what is owed), "open" (the office). A staff preview always gets "open"
 * and carries agreement_state so the strip can say where the party stands.
 *
 * Tokens only. No colour, size or family in this file or site-office.css.
 * The firm's name, logo, skin, surfaces and type come from the payload and go
 * through utils/skin.cacheBranding — never a literal.
 *
 * Payload (both endpoints):
 * {
 *   admin: bool, status: "unsigned"|"hold"|"open",
 *   agreement_state: "not_sent"|"sent"|"signed"      (admin only),
 *   link_url: str|null                                (admin only — the party's own link when one exists),
 *   agreement_url: str|null                           (unsigned — the /agreement/<token> page),
 *   firm_name: str, branding: { company_name, logo_url, firm_skin, surface_overrides, type_names, type_sentences, type_figures, type_labels },
 *   party: { membership_id, name, person, kind, trade, kind_is_terms },   kind_is_terms: the kind's requirement is purchasing terms, not the agreement
 *   project: { id, name, code, type_label, phase_label, address, maps_url, image_url },
 *   contact: { name, phone, email },
 *   also_on: [ str ],
 *   plans: [ { id, title, date } ], specs: [ { id, title, count } ],
 *   site: { access, staging },
 *   signed: [ { label, date } ],
 *   owed: [ { id, label, state: "ok"|"wait"|"due"|"overdue"|"missing", due_at } ]
 * }
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { viewAuthedFile } from '@/utils/authedFile';
import { AuthedImg } from '@/components/AuthedImg';
import { cacheBranding } from '@/utils/skin';
import '@/styles/site-office.css';
import '@/styles/bid-board.css';
import OfficeProposal from '@/components/site-office/OfficeProposal';
import OfficeStrip, { previewParam } from '@/components/site-office/OfficeStrip';
import OfficeFront from '@/components/site-office/OfficeFront';
import DrawingMarkupEditor from '@/components/DrawingMarkupEditor';
import { QR } from '@/components/QR';
import { PunchRow, punchWord, shortDate } from '@/pages/SiteOfficePunchItem';
import { ProgressPhoto, VisitGrid, VisitNotes, VisitPicker, dayLabel } from '@/pages/SiteOfficePhotos';
import KeepPortal from '../components/portal/KeepPortal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create(); // the party's link must behave the same with or without a staff token in this browser

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const firstWord = (s) => String(s || '').split(',')[0];

/**
 * SO-3a — the fold. Ten assignments are one thing that happened, so a run of
 * consecutive notices becomes one grey line; a real message always stands on
 * its own, in the order it was sent.
 */
export const foldNotices = (rows) => {
  const out = [];
  (rows || []).forEach((m) => {
    if (m.kind === 'notice') {
      const last = out[out.length - 1];
      if (last?.notice) { last.items.push(m); return; }
      out.push({ notice: true, id: m.id, items: [m] });
      return;
    }
    out.push({ notice: false, m });
  });
  return out;
};


/**
 * SO-2b — one entry in a photo grid. A video plays where it stands, a picture
 * whose file has gone says so quietly, and nothing broken is ever requested.
 */
const Shot = ({ photo, alt = '', className = 'kso-ph', testId }) => {
  if (photo.unavailable || !photo.full_url) {
    return (
      <span className={`${className} kso-ph--gone`} data-testid={testId || 'site-office-photo-gone'}>
        Picture no longer available
      </span>
    );
  }
  if (photo.media_type === 'video') {
    return (
      <span className={className} data-testid={testId || 'site-office-photo-video'}>
        <video controls preload="metadata" playsInline
          poster={photo.thumb_url || undefined} src={photo.full_url} />
      </span>
    );
  }
  return (
    <a className={className} href={photo.full_url} target="_blank" rel="noreferrer"
      data-testid={testId}>
      <img src={photo.thumb_url || photo.full_url} alt={alt} loading="lazy" />
    </a>
  );
};

// A party's door carries its own key in the URL; a staff user carries a
// session no <img> tag can send, so their thumbnails come through the app's
// authed-file helper instead.
const MessageThumb = ({ url, full, name, authed, onOpen, testId }) => (
  <button type="button" className="kso-ph" onClick={() => onOpen(full)} data-testid={testId}>
    {authed ? <AuthedImg src={url} alt={name} />
      : <img src={url} alt={name} loading="lazy" />}
  </button>
);

// DP274-P3a-5 — what came with a message: photos open in the office's own
// photo sheet, a PDF opens where the reader's device opens PDFs.
const MessageFiles = ({ message, setSheet, abs, authed }) => {
  const imgs = (message.attachments || []).filter((a) => a.kind === 'image');
  const pdfs = (message.attachments || []).filter((a) => a.kind === 'pdf');
  const open = (url) => {
    if (authed) { viewAuthedFile(url); return; }
    setSheet({
      kind: 'photos',
      title: message.name,
      photos: imgs.map((x) => ({ thumb_url: `${abs(x.url)}?w=400`, full_url: abs(x.url), caption: x.name })),
    });
  };
  return (
    <>
      {imgs.length ? (
        <div className="kso-photos" data-testid={`site-office-message-photos-${message.id}`}>
          {imgs.map((a) => (
            <MessageThumb key={a.id} url={`${abs(a.url)}?w=400`} full={abs(a.url)}
              name={a.name} authed={authed} onOpen={open}
              testId={`site-office-message-photo-${a.id}`} />
          ))}
        </div>
      ) : null}
      {pdfs.map((a) => (authed ? (
        <button type="button" className="kso-btn" key={a.id}
          onClick={() => viewAuthedFile(abs(a.url))}
          data-testid={`site-office-message-pdf-${a.id}`}>Open PDF · {a.name}</button>
      ) : (
        <a className="kso-btn" key={a.id} href={abs(a.url)} target="_blank" rel="noreferrer"
          data-testid={`site-office-message-pdf-${a.id}`}>Open PDF · {a.name}</a>
      )))}
    </>
  );
};

export default function SiteOfficePage() {
  const { token, projectId, membershipId } = useParams();
  const navigate = useNavigate();
  const isAdmin = Boolean(projectId && membershipId);
  // TM-1a — two ways to read the same office: yours, and theirs. One
  // component, two payloads; nothing about the party's page is re-drawn here.
  const [params, setParams] = useSearchParams();
  const asParty = isAdmin && params.get('view') === 'party';
  const [data, setData] = useState(null);
  // SO-9a — the ONE read token: the party's own on their link, their live token
  // in the firm's preview. Every party-side read on this page goes through it,
  // so the preview is the same page and not a page with holes in it.
  const readToken = token || data?.live_token || null;
  // ST-5 — the office link stays on this phone, so a site tag scanned later is
  // placed in this party's name instead of a stranger's.
  useEffect(() => {
    if (token) window.localStorage.setItem('kmOfficeToken', token);
  }, [token]);
  // RS-4 — what the firm expects from this party. Read-only; the hand-in is RS-5.
  const [expected, setExpected] = useState([]);
  const [closed, setClosed] = useState(null);
  const [showOwed, setShowOwed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [agrBusy, setAgrBusy] = useState(false);
  const [agrError, setAgrError] = useState('');
  // DP269-B — the thread. Text only in this package.
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState('');
  // DP274-P3c — the subcontractors this party brought, and the invite.
  const [subs, setSubs] = useState([]);
  const [sub, setSub] = useState({ company: '', name: '', email: '', phone: '', trade: '' });
  const [subBusy, setSubBusy] = useState(false);
  // SO-9b — the invite form folds away until it is asked for.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [subError, setSubError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  // DP274-P3a-5 — what is going up with the next message, before it goes.
  const [files, setFiles] = useState([]);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = '';
    const allowed = chosen.filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (allowed.length !== chosen.length) toast.error('Photos and PDFs only');
    setFiles((prev) => {
      const room = 5 - prev.length;
      if (allowed.length > room) toast.error('Five files per message');
      return [...prev, ...allowed.slice(0, Math.max(0, room))];
    });
  };

  const drop = (file) => setFiles((prev) => prev.filter((f) => f !== file));

  const load = useCallback(async () => {
    try {
      const r = isAdmin
        ? await axios.get(`${API}/projects/${projectId}/memberships/${membershipId}/site-office`
          + (asParty ? '?as_party=true' : ''))
        : await bare.get(`${API}/site-office-links/${token}`);
      setData(r.data);
      if (r.data?.branding) cacheBranding(r.data.branding);
      setClosed(null);
    } catch (e) {
      const s = e?.response?.status;
      setClosed(s === 410
        ? { title: 'This link has been replaced', text: 'A newer link was sent, or this one has expired. Use the most recent email, or ask for a new one.' }
        : { title: 'This link is not valid', text: 'Check the address in the email, or ask for a new link.' });
    }
  }, [isAdmin, projectId, membershipId, token, asParty]);

  useEffect(() => { load(); }, [load]);

  // SO-1c — the printed handshake blanks the rest of the page, and a stylesheet
  // is global to the bundle. The rule now waits for this page to say so, so it
  // cannot reach the Spec Sheet or the Spec Book.
  useEffect(() => {
    document.body.dataset.kmPrint = 'site-office';
    return () => { delete document.body.dataset.kmPrint; };
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const r = isAdmin
        ? await axios.get(`${API}/projects/${projectId}/memberships/${membershipId}/messages`)
        : await bare.get(`${API}/site-office-links/${token}/messages`);
      setMsgs(r.data?.messages || []);
    } catch (_e) { setMsgs([]); }
  }, [isAdmin, projectId, membershipId, token]);

  useEffect(() => { if (data?.status === 'open') loadMessages(); }, [data?.status, loadMessages]);

  // RS-5 — how many of this party's hand-ins have been sent back for a revision.
  const [waitingOnYou, setWaitingOnYou] = useState(0);
  // SO-9b — the whole submittals payload, for the needs list and the tile.
  const [subPaper, setSubPaper] = useState(null);

  // RS-4 — the register, read as the party reads it: what is expected and when.
  useEffect(() => {
    if (!readToken || data?.status !== 'open') return;
    bare.get(`${API}/site-office-links/${readToken}/expected`)
      .then((r) => setExpected(r.data?.expected || []))
      .catch(() => setExpected([]));
  }, [readToken, data?.status]);

  // RS-5 — and the count for the Submittals tile: rows sent back for a revision.
  useEffect(() => {
    if (!readToken || data?.status !== 'open') return;
    bare.get(`${API}/site-office-links/${readToken}/submittals`)
      .then((r) => { setSubPaper(r.data || null); setWaitingOnYou(r.data?.waiting_on_you || 0); })
      .catch(() => { setSubPaper(null); setWaitingOnYou(0); });
  }, [readToken, data?.status]);

  // TK-7b — the tasks the firm handed to this party, talked about here.
  const [tasks, setTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState({});
  const loadTasks = useCallback(async () => {
    if (!readToken) return;
    try {
      const r = await bare.get(`${API}/site-office-links/${readToken}/tasks`);
      setTasks(r.data?.tasks || []);
    } catch (_e) { setTasks([]); }
  }, [readToken]);
  useEffect(() => { if (data?.status === 'open') loadTasks(); }, [data?.status, loadTasks]);

  const sendTaskReply = async (taskId) => {
    const text = (taskDraft[taskId] || '').trim();
    if (!text) return;
    try {
      await bare.post(`${API}/site-office-links/${token}/tasks/${taskId}/messages`, { text });
      setTaskDraft((prev) => ({ ...prev, [taskId]: '' }));
      loadTasks();
    } catch (_e) { /* the card re-reads; nothing is lost */ }
  };

  const tickTaskStep = async (taskId, stepId, completed) => {
    try {
      await bare.post(`${API}/site-office-links/${token}/tasks/${taskId}/steps/${stepId}`, { completed });
      loadTasks();
    } catch (_e) { /* the card re-reads */ }
  };

  const [sheet, setSheet] = useState(null);
  // SO-3b — which visit the Photos card is showing; the latest until asked.
  const [visitPick, setVisitPick] = useState('');
  // DP275-P4c — the field sheet code being held up for a crew to scan.
  const [showQr, setShowQr] = useState(null);

  useEffect(() => {
    if (!showQr) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowQr(null); };
    document.addEventListener('keydown', onKey);
    let lock = null;
    if (navigator.wakeLock?.request) {
      navigator.wakeLock.request('screen').then((l) => { lock = l; }).catch(() => {});
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      lock?.release?.().catch(() => {});
    };
  }, [showQr]);
  // DP274-P3a-2 — the sheet this party is marking up, and the marks on it.
  const [markupSheet, setMarkupSheet] = useState(null);
  const [marks, setMarks] = useState([]);

  const markBase = (x) => `${API}/site-office-links/${readToken}/drawings/${x.id}/versions/${x.version_id}/markups`;

  const openMarkup = async (x) => {
    try {
      const r = await bare.get(markBase(x));
      setMarks(r.data?.markups || []);
      setMarkupSheet(x);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That sheet did not open. Try again.');
    }
  };

  const loadSubs = useCallback(async () => {
    if (!readToken) return;
    try {
      const r = await bare.get(`${API}/site-office-links/${readToken}/subcontractors`);
      setSubs(r.data?.rows || []);
    } catch { setSubs([]); }
  }, [readToken]);

  useEffect(() => { if (data?.can_invite) loadSubs(); }, [data?.can_invite, loadSubs]);

  const invite = async () => {
    setSubError('');
    try {
      setSubBusy(true);
      const r = await bare.post(`${API}/site-office-links/${token}/subcontractors`, sub);
      setSubs((prev) => [...prev, r.data]);
      setSub({ company: '', name: '', email: '', phone: '', trade: '' });
    } catch (e) {
      setSubError(e?.response?.data?.detail || 'Could not send that invite. Try again.');
    } finally { setSubBusy(false); }
  };

  const closeMarkup = () => { setMarkupSheet(null); setMarks([]); load(); loadMessages(); };

  // DP274-P3a-4 — the answers email links to one sheet, so ?sheet= opens that
  // sheet's Mark up card, exactly as clicking its row would.
  const wantedSheet = new URLSearchParams(window.location.search).get('sheet');
  const openedWanted = useRef(false);
  useEffect(() => {
    if (openedWanted.current || !wantedSheet || data?.status !== 'open') return;
    const plan = (data.plans || []).find((p) => p.id === wantedSheet && p.version_id);
    if (!plan) return;
    openedWanted.current = true;
    openMarkup(plan);
  }, [data, wantedSheet]);

  const postMarks = async (x, dirty, { send, message } = {}) => {
    try {
      const r = await bare.post(markBase(x), { markups: dirty, send: Boolean(send), message: message || '' });
      if (send) toast.success(`Round ${r.data.round} is with ${firm}`);
      return r.data.markups || [];
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not save. Try again.');
      return null;
    }
  };

  const replyToMark = async (x, markId, text) => {
    const r = await bare.post(`${markBase(x)}/${markId}/replies`, { text });
    return r.data;
  };
  // DP272-P1b — the party sends their own paperwork from here.
  const [sendDoc, setSendDoc] = useState(null); // the owed id whose row is open
  const [docFile, setDocFile] = useState(null);
  const [docExpiry, setDocExpiry] = useState('');
  const [docSending, setDocSending] = useState(false);

  const sendDocument = async (o) => {
    if (!docFile) { toast.error('Choose the file first.'); return; }
    if (o.annual && !docExpiry) { toast.error('Enter the expiry date shown on the certificate.'); return; }
    setDocSending(true);
    try {
      const fd = new FormData();
      fd.append('file', docFile);
      fd.append('doc_type', o.id);
      if (docExpiry) fd.append('expiration_date', docExpiry);
      const r = await bare.post(`${API}/site-office-links/${token}/documents`, fd);
      setData((prev) => ({ ...prev, owed: r.data.owed, status: r.data.status }));
      setSendDoc(null); setDocFile(null); setDocExpiry('');
      toast.success('Received — thank you');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not send. Try again.');
    } finally {
      setDocSending(false);
    }
  };

  // DP-1a — who is signing, and under what title.
  const [signName, setSignName] = useState('');
  const [signTitle, setSignTitle] = useState('');

  const signDocument = async (o) => {
    if (!signName.trim()) { toast.error('Type your name to sign.'); return; }
    setDocSending(true);
    try {
      const r = await bare.post(`${API}/site-office-links/${token}/sign/${o.id}`,
        { name: signName.trim(), title: signTitle.trim() });
      setData((prev) => ({ ...prev, owed: r.data.owed, status: r.data.status }));
      setSendDoc(null); setSignName(''); setSignTitle('');
      toast.success('Signed — thank you');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not send. Try again.');
    } finally { setDocSending(false); }
  };

  const owedList = (testid) => (
    <ul className="kso-owed" data-testid={testid}>
      {(data.owed || []).map((o) => (
        <li key={o.id}>
          <span>{o.label}</span>
          <span className={`d ${o.state === 'overdue' || o.state === 'missing' ? 'late' : o.state === 'ok' ? 'ok' : 'wait'}`}>
            {o.state === 'ok' ? 'on file' : o.state === 'overdue' ? 'overdue' : o.state === 'missing' ? 'missing' : o.due_at ? `due ${fmtDate(o.due_at)}` : 'needed'}
          </span>
          {o.state === 'ok'
            ? (o.file_url
              ? <a className="kso-btn" href={abs(o.file_url)} target="_blank" rel="noreferrer" data-testid={`office-view-${o.id}`}>View</a>
              : <span />)
            : o.sign
              ? (
                <button type="button" className="kso-btn brand" data-testid={`office-sign-${o.id}`}
                  disabled={isAdmin} title={isAdmin ? `Only ${(data.party || {}).name} can do this` : undefined}
                  onClick={() => { setSendDoc(sendDoc === o.id ? null : o.id); setSignName(''); setSignTitle(''); }}>
                  Read and sign
                </button>
              )
              : (
                <button type="button" className="kso-btn" data-testid={`office-send-${o.id}`}
                  disabled={isAdmin} title={isAdmin ? `Only ${(data.party || {}).name} can do this` : undefined}
                  onClick={() => { setSendDoc(sendDoc === o.id ? null : o.id); setDocFile(null); setDocExpiry(''); }}>
                  Send it
                </button>
              )}
          {sendDoc === o.id && o.sign ? (
            <div className="kso-sign" data-testid={`office-sign-row-${o.id}`}>
              {(o.text || '').split('\n').filter(Boolean).map((p, i) => (
                <p key={i} className="kso-sign__p">{p}</p>
              ))}
              <input className="kso-in" placeholder="Your name, as the person authorised to sign"
                value={signName} onChange={(e) => setSignName(e.target.value)}
                data-testid={`office-sign-name-${o.id}`} />
              <input className="kso-in" placeholder="Title (optional)"
                value={signTitle} onChange={(e) => setSignTitle(e.target.value)}
                data-testid={`office-sign-title-${o.id}`} />
              <button type="button" className="kso-btn brand" disabled={docSending}
                data-testid={`office-sign-go-${o.id}`} onClick={() => signDocument(o)}>
                {docSending ? 'Signing…' : 'I confirm and sign'}
              </button>
            </div>
          ) : null}
          {sendDoc === o.id && !o.sign ? (
            <div className="kso-send" data-testid={`office-send-row-${o.id}`}>
              <input type="file" accept=".pdf,image/*" data-testid={`office-file-${o.id}`}
                onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
              {o.annual ? (
                <label>Expires <input type="date" value={docExpiry} data-testid={`office-expiry-${o.id}`}
                  onChange={(e) => setDocExpiry(e.target.value)} /></label>
              ) : null}
              <button type="button" className="kso-btn brand" disabled={docSending}
                data-testid={`office-send-go-${o.id}`} onClick={() => sendDocument(o)}>
                {docSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );

  const openSpecs = async (x) => {
    const r = await axios.get(`${API}${x.url.replace('/api', '')}`).catch(() => null)
      || await bare.get(`${API}${x.url.replace('/api', '')}`).catch(() => null);
    setSheet({ kind: 'specs', title: x.title, items: r?.data?.items || [] });
  };

  // Both URLs arrive relative; the office may be served from another origin.
  const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

  const send = async () => {    const text = draft.trim();
    if (!text && !files.length) return;
    setSending(true);
    setSendError('');
    try {
      const firmBase = `${API}/projects/${projectId}/memberships/${membershipId}/messages`;
      const partyBase = `${API}/site-office-links/${token}/messages`;
      let r;
      if (files.length) {
        const form = new FormData();
        form.append('text', text);
        files.forEach((f) => form.append('files', f));
        r = isAdmin ? await axios.post(`${firmBase}/with-files`, form)
          : await bare.post(`${partyBase}/with-files`, form);
      } else {
        r = isAdmin ? await axios.post(firmBase, { text })
          : await bare.post(partyBase, { text });
      }
      setMsgs((prev) => [...prev, r.data]);
      setDraft('');
      setFiles([]);
    } catch (e) {
      setSendError(e?.response?.data?.detail || 'That did not send. Try again.');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!data?.link_url) return;
    try { await navigator.clipboard.writeText(data.link_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* clipboard blocked */ }
  };

  // SO-1a — the one sender: the same route the team row uses. It rotates the
  // party's link and emails the agreement; nothing here sends anything itself.
  const sendAgreement = async () => {
    setAgrBusy(true);
    setAgrError('');
    try {
      await axios.post(`${API}/projects/${projectId}/memberships/${membershipId}/send-agreement`);
    } catch (e) {
      // The link is rotated and the agreement marked sent before the email
      // leaves, so a provider refusal is reported without hiding the state.
      const detail = e?.response?.data?.detail;
      setAgrError(typeof detail === 'string' && detail.length < 200
        ? `Sent, but the email was refused: ${detail}`
        : 'Sent, but the email could not be delivered. Try Resend.');
    }
    try {
      await load();
    } finally {
      setAgrBusy(false);
    }
  };

  if (closed) {
    return (
      <main className="kso-page" data-testid="site-office">
        <div className="kso-wrap"><div className="kso-gate"><h1>{closed.title}</h1><p>{closed.text}</p></div></div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="kso-page" data-testid="site-office">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const firm = data.firm_name || '';
  const firmShort = firstWord(firm);
  const p = data.party || {};
  const pr = data.project || {};
  // SO-9b — the project picture is served through the party's own door, so the
  // firm's preview must read it on their token; with no link yet, no picture.
  const bandImg = pr.image_url
    ? (String(pr.image_url).includes('/project-image')
      ? (readToken ? `${API}/site-office-links/${readToken}/project-image` : null)
      : pr.image_url)
    : null;
  const c = data.contact || {};
  const owedOpen = (data.owed || []).filter((o) => o.state !== 'ok');
  const shotVisit = (data.photos || []).find((v) => v.id === visitPick) || (data.photos || [])[0];
  // SO-3c — the token these cards link by: the party's own on their link, and
  // in As-they-see-it the live token the admin read handed over. When there is
  // neither (Your view), the rows are plain and go nowhere.
  const partyToken = token || (asParty ? data.live_token : null);
  // SO-9a — R2: acting as the party is the party's alone; and R3: a sub-page
  // opened from the preview carries the strip with it.
  const onlyParty = isAdmin ? `Only ${p.name} can do this` : undefined;
  const sub_ = (path) => (isAdmin ? `${path}?preview=${previewParam(projectId, membershipId)}` : path);

  const strip = isAdmin ? (
    <OfficeStrip projectId={projectId} membershipId={membershipId} projectName={data.project?.name}
      partyName={p.name} view={asParty ? 'party' : 'yours'}
      onView={(v) => { const n = new URLSearchParams(params); if (v === 'party') n.set('view', 'party'); else n.delete('view'); setParams(n); }}
      line={<>Previewing as <b>{p.name}</b> · {p.kind}{p.trade ? ` · ${p.trade}` : ''}{data.agreement_state ? <> · agreement <b>{data.agreement_state === 'signed' ? 'signed' : data.agreement_state === 'sent' ? 'sent · awaiting signature' : 'not yet sent'}</b></> : null}</>}>
      {/* SO-1a — the agreement's state, and the one control that changes it.
          As-they-see-it is built as the party, which does not carry the state. */}
      {data.agreement_state ? (
      <div className="kso-admin__agr" data-testid="site-office-agreement">
        {data.agreement_state === 'signed' ? (
          <>
            <span data-testid="site-office-agreement-signed">
              Signed {fmtDate(data.agreement?.signed_at)}
            </span>
            {data.agreement?.url ? (
              <a className="kso-abtn" href={data.agreement.url} target="_blank" rel="noreferrer"
                data-testid="site-office-agreement-open">Open</a>
            ) : null}
          </>
        ) : (
          <>
            <button type="button" className="kso-abtn" disabled={agrBusy}
              onClick={sendAgreement} data-testid="site-office-send-agreement">
              {agrBusy ? 'Sending…' : data.agreement_state === 'sent' ? 'Resend agreement' : 'Send agreement'}
            </button>
            {data.agreement_state === 'sent' && data.agreement?.sent_at ? (
              <span data-testid="site-office-agreement-sent">
                Sent {fmtDate(data.agreement.sent_at)}{data.agreement.email ? ` to ${data.agreement.email}` : ''}
              </span>
            ) : null}
            {data.agreement_state === 'sent' && data.link_url ? (
              <span className="kso-admin__url" data-testid="site-office-agreement-link">{data.link_url}</span>
            ) : null}
            {agrError ? <span className="kso-admin__err" data-testid="site-office-agreement-error">{agrError}</span> : null}
          </>
        )}
      </div>
      ) : null}
      {data.link_url ? (
        <button type="button" className="kso-abtn" onClick={copyLink} data-testid="site-office-copy-link">{copied ? 'Copied' : 'Copy their link'}</button>
      ) : null}
      <button type="button" className="kso-abtn" data-testid="site-office-ask-price"
        onClick={() => navigate(`/projects/${projectId}/ask-for-a-price?membership=${membershipId}`)}>Ask for a price</button>
      {p.contact_id ? (
        <button type="button" className="kso-abtn" data-testid="site-office-edit-contact"
          onClick={() => navigate(`/projects/${projectId}/desk?tab=team&edit_contact=${p.contact_id}`)}>Edit contact</button>
      ) : null}
    </OfficeStrip>
  ) : null;

  const head = (
    <header className="kso-head">
      {data.branding?.logo_url ? <img className="kso-head__logo" src={data.branding.logo_url} alt="" /> : null}
      <div className="kso-head__k"><b>{firm}</b> · Site Office</div>
      <KeepPortal
        portalKey={`so-${token}`}
        manifestHref={`${API}/site-office-links/${token}/manifest.webmanifest`}
        title="Site Office"
        iconHref={data.branding?.company_id ? `${API}/portal-icon/${data.branding.company_id}?s=192` : null}
        buttonClass="kso-btn"
      />
      <div className="kso-head__who">
        <div className="n">{p.name}</div>
        <div className="r">{[p.person, p.kind, p.trade].filter(Boolean).join(' · ')}</div>
      </div>
    </header>
  );

  const foot = (
    <div className="kso-foot">{firm} · this link is personal to {p.name} · {pr.name} only</div>
  );

  if (data.status === 'unsigned') {
    return (
      <main className="kso-page" data-testid="site-office">
        <div className="kso-wrap">
          {strip}{head}
          <div className="kso-gate" data-testid="site-office-unsigned">
            <h1>Before you can see the project, one thing</h1>
            <p>{firm} has added <b>{p.name}</b> to <b>{pr.name}</b>. Read and {p.kind_is_terms ? 'acknowledge the purchasing terms' : 'sign the agreement'} to open your Site Office. Nothing else on the project shows until then.</p>
            <div className="kso-acts">
              {data.agreement_url ? <a className="kso-btn brand" href={data.agreement_url} data-testid="site-office-to-agreement">Open the {p.kind_is_terms ? 'terms' : 'agreement'}</a> : null}
            </div>
          </div>
          {foot}
        </div>
      </main>
    );
  }

  if (data.status === 'hold') {
    return (
      <main className="kso-page" data-testid="site-office">
        <div className="kso-wrap">
          {strip}{head}
          <div className="kso-gate hold" data-testid="site-office-hold">
            <h1>On hold until these arrive</h1>
            <p>Your agreement is signed. Work on <b>{pr.name}</b> is paused until the documents below are on file with {firmShort}; the Site Office reopens the moment they are.</p>
            {owedList('site-office-hold-owed')}
            <p className="kso-hint">Send each file here; it goes straight onto your record with {firmShort}.</p>
          </div>
          {foot}
        </div>
      </main>
    );
  }

  // DP274-P3a-2 — marking up a shared sheet, in place of the office grid.
  if (markupSheet) {
    const x = markupSheet;
    const myRounds = marks.filter((m) => m.author?.kind === 'party' && m.round);
    const nextRound = myRounds.reduce((n, m) => Math.max(n, m.round || 0), 0) + 1;
    return (
      <main className="kso-page" data-testid="site-office-markup">
        <div className="kso-wrap">
          {strip}{head}
          <section className="kso-card w12">
            <div className="kso-card__hd">
              <span>Mark up · {x.title}{x.rev ? ` · ${x.rev}` : ''}</span>
              <span className="n">{x.open_notes ? `${x.open_notes} open notes` : 'no notes yet'}</span>
            </div>
            <div className="kso-card__bd">
              <DrawingMarkupEditor
                embedded
                imageUrl={abs(x.url)}
                fileKind={x.kind || 'pdf'}
                existingMarkups={marks}
                authorKind="party"
                readOnly={false}
                drawingTitle={x.title}
                drawingNumber={''}
                projectName={pr.name || ''}
                versionNumber={x.rev || ''}
                onClose={closeMarkup}
                onSave={() => {}}
                onStatus={null}
                onReply={(markId, text) => replyToMark(x, markId, text)}
                onDeleteMark={(markId) => bare.delete(`${markBase(x)}/${markId}`)}
                saveButtons={[
                  { id: 'save-later', label: 'Save for later', disabled: isAdmin, title: onlyParty,
                    action: (dirty) => postMarks(x, dirty, { send: false }) },
                  { id: 'send-round', label: `Send round ${nextRound} to ${firmShort || firm}`,
                    brand: true, asks: `A line for ${firmShort || firm} (optional)`,
                    confirm: `Send round ${nextRound}`, disabled: isAdmin, title: onlyParty,
                    action: (dirty, message) => postMarks(x, dirty, { send: true, message }) },
                ]}
              />
            </div>
          </section>
        </div>
      </main>
    );
  }

  if (sheet) {
    return (
      <main className="kso-page" data-testid="site-office-sheet">
        <div className="kso-wrap">
          {strip}{head}
          <div className="kso-gate">
            <div className="kso-acts"><button type="button" className="kso-btn" onClick={() => setSheet(null)} data-testid="site-office-sheet-back">Back to the office</button></div>
            <h1>{sheet.title}</h1>
            {sheet.kind === 'photos' ? (
              <div className="kso-photos">
                {sheet.photos.map((p, i) => (
                  <Shot key={p.thumb_url || `sheet-${i}`} photo={p} alt={p.caption || ''}
                    testId={`site-office-sheet-photo-${i}`} />
                ))}
              </div>
            ) : (
              <div>
                {sheet.items.length === 0 ? <p className="kso-hint">Nothing in this category yet.</p> : null}
                {sheet.items.map((it) => (
                  <div className="kso-row" key={it.id}>
                    {it.primary_image ? (
                      <div className="kso-ph" style={{ width: 64, aspectRatio: '1/1' }}>
                        <img src={it.primary_image.startsWith('/api') ? `${API}${it.primary_image.replace(/^\/api/, '')}` : it.primary_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : null}
                    <div className="t"><b>{it.spec_num ? `${it.spec_num} · ` : ''}{it.title}</b>
                      <span className="src">{[it.manufacturer, it.model_number, it.finish, it.color, it.dimensions].filter(Boolean).join(' · ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="kso-page" data-testid="site-office">
      <div className="kso-wrap">
        {strip}{head}
        <section className="kso-band" data-testid="site-office-band">
          <div className="kso-band__lead">
            {bandImg
              ? <img className="kso-band__img" src={bandImg} alt="" />
              : <div className="kso-band__img">{pr.name}</div>}
            <div>
              <div className="kso-band__code">{[pr.code, pr.type_label, pr.phase_label].filter(Boolean).join(' · ')}</div>
              <div className="kso-band__name">{pr.name}</div>
              <div className="kso-band__addr">
                {pr.address}{pr.maps_url ? <> · <a href={pr.maps_url} target="_blank" rel="noreferrer">open in maps</a></> : null}
              </div>
            </div>
          </div>
          <div className="kso-band__contact">
            <span className="k">Your contact at {firmShort}</span>
            {c.name}<br />
            {c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : null}{c.phone && c.email ? ' · ' : ''}{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null}
          </div>
          {(data.also_on || []).length ? (
            <div className="kso-also">Also with {firmShort} on: <b>{data.also_on.join(', ')}</b> — each job has its own Site Office and its own link.</div>
          ) : null}
        </section>
        <div className="kso-grid">
          {/* SO-9b — the catalogue: what the firm needs, then every
              engagement as a tile. The detail cards that have no page of
              their own sit below it, each with the id its tile points at. */}
          <OfficeFront data={{ ...data, tasks, subs }} firmShort={firmShort} subs={subPaper}
            readToken={readToken} sub={sub_} locked={isAdmin} partyName={p.name} />
          {!readToken ? (
            <section className="kso-card w12" id="office-tasks" data-testid="so-tasks">
              <div className="kso-card__hd">Tasks for you<span className="n">nothing yet</span></div>
              <div className="kso-card__bd">
                <span className="k">Available once their link exists</span>
              </div>
            </section>
          ) : (
            <section className="kso-card w12" id="office-tasks" data-testid="so-tasks">
              <div className="kso-card__hd">
                Tasks for you
                <span className="n">{tasks.length ? `${tasks.length} handed to you` : 'nothing right now'}</span>
              </div>
              <div className="kso-card__bd">
                {tasks.length === 0 ? (
                  <p className="kso-hint" style={{ margin: 0 }}>Nothing handed to you right now.</p>
                ) : tasks.map((t) => (
                  <div className="kso-row" key={t.id} data-testid={`so-task-${t.id}`} style={{ display: 'block' }}>
                    <div className="t">
                      <b>{t.title}</b>
                      <small>{[t.due_date ? `due ${shortDate(t.due_date)}` : '',
                        t.estimate_minutes ? `${t.estimate_minutes} min` : ''].filter(Boolean).join(' · ')}</small>
                    </div>
                    {(t.subtasks || []).map((s) => (
                      <label className="kso-tick" key={s.id} data-testid={`so-task-step-${s.id}`}>
                        <input type="checkbox" checked={!!s.completed} disabled={isAdmin} title={onlyParty}
                          onChange={(e) => tickTaskStep(t.id, s.id, e.target.checked)} />
                        <span>{s.title}</span>
                      </label>
                    ))}
                    {(t.messages || []).map((m) => (
                      <div className={`kso-msg${m.party === 'contractor' ? ' me' : ''}`} key={m.id}
                        data-testid={`so-task-msg-${m.id}`}>
                        <span className="w">{m.author_name} · {fmtDate(m.created_at)}</span>
                        <span className="b">{m.text}</span>
                      </div>
                    ))}
                    <div className="kso-compose">
                      <input className="kso-in" placeholder={`Reply about ${t.title}…`}
                        value={taskDraft[t.id] || ''}
                        onChange={(e) => setTaskDraft((prev) => ({ ...prev, [t.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendTaskReply(t.id); } }}
                        data-testid={`so-task-input-${t.id}`} />
                      <button type="button" className="kso-btn brand"
                        disabled={isAdmin || !(taskDraft[t.id] || '').trim()} title={onlyParty}
                        onClick={() => sendTaskReply(t.id)}
                        data-testid={`so-task-send-${t.id}`}>Send</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {data.requests?.length ? (
            <section className="kso-card w12" id="office-price" data-testid="site-office-requests">
              <div className="kso-card__hd">Price request{data.requests.length > 1 ? 's' : ''}<span className="n">{data.requests.length}</span></div>
              <div className="kso-card__bd">
                {data.requests.map((q) => (
                  <OfficeProposal key={q.id} req={q} token={isAdmin ? null : token} firmShort={firmShort} firmName={firm} onSent={load} />
                ))}
              </div>
            </section>
          ) : null}
          {(data.plans || []).length ? (
            <section className="kso-card w12" id="office-plans" data-testid="site-office-plans">
              <div className="kso-card__hd">Plans &amp; drawings<span className="n">{data.plans.length} shared</span></div>
              <div className="kso-card__bd">
                {data.plans.map((x) => (
                  <div className="kso-row" key={x.id}>
                    <div className="t"><b>{x.title}</b>{x.rev ? <> · {x.rev}</> : null}<span className="src">{x.set_name}</span></div>
                    <span className="m">{fmtDate(x.date)}</span>
                    {x.open_notes > 0 ? <span className="kso-tag wait">{x.open_notes} open notes</span> : null}
                    {x.my_draft ? <span className="kso-tag you">draft</span> : null}
                    {/* DP274-P3a-4 — their notes with an answer, and which of
                        those the firm has just sent them a word about. */}
                    {x.new_answers > 0 ? (
                      <span className="kso-tag you" data-testid={`site-office-new-answers-${x.id}`}>{x.new_answers} new</span>
                    ) : x.answered > 0 ? (
                      <span className="kso-tag ok" data-testid={`site-office-answered-${x.id}`}>{x.answered} answered</span>
                    ) : null}
                    {x.url ? (
                      <a className="kso-btn" href={abs(x.url)} target="_blank" rel="noreferrer" data-testid={`site-office-plan-${x.id}`}>Open</a>
                    ) : <span className="m">unavailable</span>}
                    {x.url && x.version_id ? (
                      <button type="button" className="kso-btn brand" onClick={() => openMarkup(x)}
                        data-testid={`site-office-markup-${x.id}`}>Mark up</button>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <section className="kso-card w12" id="office-messages" data-testid="site-office-messages">
            <div className="kso-card__hd">Messages · this project<span className="n">{msgs.length ? `${msgs.length} in thread` : 'start the thread'}</span></div>
            <div className="kso-card__bd">
              {msgs.length === 0 ? (
                <p className="kso-hint" style={{ margin: 0 }}>Questions about the job go here and reach {firmShort} directly. Nothing else on this page is a message.</p>
              ) : foldNotices(msgs).map((row) => (row.notice ? (
                <a className="kso-msg sys" key={row.id}
                  href={readToken ? sub_(`/site-office/${readToken}/punch`) : undefined}
                  data-testid={`site-office-notices-${row.id}`}>
                  <span className="b">
                    {row.items.length} punch item{row.items.length === 1 ? '' : 's'} assigned
                    {' · '}{shortDate(row.items[row.items.length - 1].at)} · see Punch list
                  </span>
                </a>
              ) : (
                <div className={`kso-msg${row.m.from === 'party' ? ' me' : ''}`} key={row.m.id}>
                  <span className="w">{row.m.name} · {fmtDate(row.m.at)}</span>
                  <span className="b">{row.m.text}</span>
                  {row.m.attachments?.length ? <MessageFiles message={row.m} setSheet={setSheet} abs={abs} authed={isAdmin} /> : null}
                </div>
              )))}
              {sendError ? <p className="kso-hint kso-hint--late">{sendError}</p> : null}
              {files.length ? (
                <div className="kso-msg" data-testid="site-office-file-chips">
                  {files.map((f) => (
                    <span className="kso-tag" key={f.name}>
                      {f.name}
                      <button type="button" className="x" onClick={() => drop(f)} aria-label="Remove">×</button>
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="kso-compose kso-compose--files">
                <input
                  className="kso-in"
                  placeholder={isAdmin ? `Reply as ${firmShort}…` : `Reply to ${firmShort}…`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="site-office-message-input"
                />
                <label className="kso-btn" data-testid="site-office-attach">
                  Photo or PDF
                  <input type="file" accept="image/*,application/pdf" multiple hidden onChange={pick} />
                </label>
                <label className="kso-btn kso-cam" data-testid="site-office-camera">
                  Camera
                  <input type="file" accept="image/*" capture="environment" hidden onChange={pick} />
                </label>
                <button type="button" className="kso-btn brand" disabled={sending || (!draft.trim() && !files.length)} onClick={send} data-testid="site-office-message-send">Send</button>
              </div>
            </div>
          </section>
          <section className="kso-card w12" id="office-paperwork" data-testid="site-office-site">
            <div className="kso-card__hd">Site information</div>
            <div className="kso-card__bd">
              {data.site?.access ? <div className="kso-row"><div className="t"><b>Property access</b> — {data.site.access}</div></div> : null}
              {data.site?.staging ? <div className="kso-row"><div className="t"><b>Parking &amp; staging</b> — {data.site.staging}</div></div> : null}
              {(data.signed || []).map((s, i) => (
                <div className="kso-row" key={i}>
                  <div className="t">
                    <b>Documents you have signed</b> — {s.label}
                    {s.method === 'paper' ? ` · signed on paper ${fmtDate(s.date)}` : ` · ${fmtDate(s.date)}`}
                    {s.file_url ? (
                      <>
                        {' '}
                        <button type="button" className="kso-btn" data-testid="site-office-signed-view"
                          onClick={() => window.open(abs(s.file_url), '_blank')}>View</button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
              {(data.owed || []).length ? (
                <>
                  <div className="kso-acts">
                    <button type="button" className="kso-btn" onClick={() => setShowOwed((v) => !v)} data-testid="site-office-owed-btn">
                      Paperwork{owedOpen.length ? ` · ${owedOpen.length} still needed` : ' · on file'}
                    </button>
                  </div>
                  {showOwed ? (
                    <>
                      {owedList('site-office-owed')}
                      <p className="kso-hint">Send each file here; it goes straight onto your record with {firmShort}.</p>
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </section>
          {data.can_invite ? (
            <section className="kso-card w12" id="office-subs" data-testid="site-office-subs">
              <div className="kso-card__hd">Your subcontractors on this project<span className="n">{subs.length ? `${subs.length} invited` : 'none yet'}</span></div>
              <div className="kso-card__bd">
                <div className="kso-subs">
                  {subs.map((r) => (
                    <div className="kso-sub" key={r.id} data-testid={`site-office-sub-${r.id}`}>
                      <div className="n">{r.company_name}{r.name ? ` · ${r.name}` : ''}<small>{r.trade || 'Subcontractor'} · invited {fmtDate(r.invited_at)}</small></div>
                      <span className={`kso-tag ${r.standing === 'open' ? 'ok' : r.standing === 'hold' ? 'late' : 'wait'}`}>{r.standing_text}</span>
                    </div>
                  ))}
                </div>
                {isAdmin ? (
                  <span className="k" data-testid="site-office-sub-invite-locked">{onlyParty}</span>
                ) : !inviteOpen ? (
                  <div className="kso-acts">
                    <button type="button" className="kso-btn" onClick={() => setInviteOpen(true)}
                      data-testid="site-office-sub-invite-open">Invite a sub</button>
                  </div>
                ) : (
                  <div className="kso-form">
                    <input className="kso-in" placeholder="Company" value={sub.company} onChange={(e) => setSub({ ...sub, company: e.target.value })} data-testid="site-office-sub-company" />
                    <input className="kso-in" placeholder="Contact name" value={sub.name} onChange={(e) => setSub({ ...sub, name: e.target.value })} data-testid="site-office-sub-name" />
                    <input className="kso-in" placeholder="Email" type="email" value={sub.email} onChange={(e) => setSub({ ...sub, email: e.target.value })} data-testid="site-office-sub-email" />
                    <input className="kso-in" placeholder="Mobile" value={sub.phone} onChange={(e) => setSub({ ...sub, phone: e.target.value })} data-testid="site-office-sub-phone" />
                    <input className="kso-in" placeholder="Trade (e.g. Drywall)" list="kso-trades" value={sub.trade} onChange={(e) => setSub({ ...sub, trade: e.target.value })} data-testid="site-office-sub-trade" />
                    <div className="w">
                      <button type="button" className="kso-btn brand" disabled={subBusy || !sub.company.trim() || !sub.name.trim() || !sub.email.trim()} onClick={invite} data-testid="site-office-sub-invite">Invite — sends {firmShort}'s agreement</button>
                      <span className="kso-hint" style={{ margin: 0 }}>They sign the same agreement you did, owe the same documents, and get their own office for this project. {firmShort} sees them on the team as invited by you.</span>
                    </div>
                  </div>
                )}
                {subError ? <p className="kso-hint kso-hint--late" role="alert">{subError}</p> : null}
              </div>
            </section>
          ) : null}
          {data.note ? (
            <section className="kso-card w12" data-testid="site-office-note">
              <div className="kso-card__hd">From {firmShort}</div>
              <div className="kso-card__bd"><div className="kso-est__ask">{data.note}</div></div>
            </section>
          ) : null}
          {data.field_sheets?.length ? (
            <section className="kso-card w12" data-testid="site-office-field-sheets">
              <div className="kso-card__hd">Field sheets for your crew<span className="n">{data.field_sheets.length} live</span></div>
              <div className="kso-card__bd">
                {data.field_sheets.map((s) => (
                  <div className="kso-row" key={s.id}>
                    <div className="t"><b>{s.name}</b> — {s.carries}<span className="src">updated {fmtDate(s.updated_at)}</span></div>
                    <button type="button" className="kso-btn brand" onClick={() => setShowQr(s)}
                      data-testid={`site-office-show-qr-${s.id}`}>Show QR</button>
                  </div>
                ))}
                <p className="kso-hint" style={{ margin: 'var(--km-s2) 0 0' }}>Your crew scans the code with a phone camera and sees the current sheet — no login, nothing to send back. It updates when {firmShort} updates it.</p>
              </div>
            </section>
          ) : null}
        </div>
        {foot}
      </div>
      {showQr ? (
        <div className="kfs-full" data-testid="site-office-qr-full"
          onClick={(e) => { if (e.target === e.currentTarget) setShowQr(null); }}>
          <span className="kfs-page__k">Scan for the {showQr.name} sheet</span>
          <div className="kfs-qrbig">
            <QR value={showQr.url} size={Math.min(window.innerWidth - 96, 328)} />
          </div>
          <span className="kfs-page__t">{pr.name} · {showQr.name}</span>
          <span className="kfs-meta">{firmShort} · updated {fmtDate(showQr.updated_at)}</span>
          <button type="button" className="kso-btn" onClick={() => setShowQr(null)}
            data-testid="site-office-qr-done">Done</button>
        </div>
      ) : null}
    </main>
  );
}
